import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FlaskConical, Plus, Play, Pause, Trophy, BarChart3, Users,
  TrendingUp, TrendingDown, Target, Eye, MousePointer, DollarSign,
  RefreshCw, Settings, Trash2, ChevronRight, X, Check, Edit3,
  Layers, ArrowLeftRight, Award, Clock, Percent
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLORS = {
  draft: 'bg-slate-500/20 text-slate-400 border-slate-500/30',
  running: 'bg-green-500/20 text-green-400 border-green-500/30',
  paused: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  completed: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
};

const ABTesting = () => {
  const [tests, setTests] = useState([]);
  const [stats, setStats] = useState(null);
  const [funnels, setFunnels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTest, setSelectedTest] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [createForm, setCreateForm] = useState({
    funnel_id: '',
    page_id: '',
    name: '',
    description: '',
    variants: [
      { name: 'Control (A)', traffic: 50 },
      { name: 'Variant B', traffic: 50 }
    ],
    auto_winner_threshold: 100
  });
  const [pages, setPages] = useState([]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('auth_token');
      const headers = { Authorization: `Bearer ${token}` };

      const [testsRes, statsRes, funnelsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/ab-tests`, { headers }),
        axios.get(`${BACKEND_URL}/api/ab-tests/stats`, { headers }),
        axios.get(`${BACKEND_URL}/api/funnels`, { headers }),
      ]);

      setTests(testsRes.data);
      setStats(statsRes.data);
      setFunnels(funnelsRes.data.funnels || []);
    } catch (error) {
      console.error('Failed to fetch A/B tests:', error);
      toast.error('Failed to load A/B tests');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const fetchPages = async (funnelId) => {
    try {
      const token = localStorage.getItem('auth_token');
      const res = await axios.get(
        `${BACKEND_URL}/api/funnels/${funnelId}/pages`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPages(res.data.pages || []);
    } catch (error) {
      console.error('Failed to fetch pages:', error);
      setPages([]);
    }
  };

  const handleFunnelChange = (funnelId) => {
    setCreateForm({ ...createForm, funnel_id: funnelId, page_id: '' });
    if (funnelId) {
      fetchPages(funnelId);
    } else {
      setPages([]);
    }
  };
  
  // Add a new variant (max 4)
  const addVariant = () => {
    if (createForm.variants.length >= 4) {
      toast.error('Maximum 4 variants allowed');
      return;
    }
    const variantLetters = ['A', 'B', 'C', 'D'];
    const newVariantLetter = variantLetters[createForm.variants.length];
    const newTraffic = Math.floor(100 / (createForm.variants.length + 1));
    
    // Redistribute traffic equally
    const updatedVariants = createForm.variants.map(v => ({
      ...v,
      traffic: newTraffic
    }));
    
    // Add remainder to last variant if needed
    const totalSoFar = updatedVariants.reduce((sum, v) => sum + v.traffic, 0);
    const remainder = 100 - totalSoFar - newTraffic;
    
    setCreateForm({
      ...createForm,
      variants: [
        ...updatedVariants,
        { name: `Variant ${newVariantLetter}`, traffic: newTraffic + remainder }
      ]
    });
  };
  
  // Remove a variant
  const removeVariant = (index) => {
    if (createForm.variants.length <= 2) {
      toast.error('Minimum 2 variants required');
      return;
    }
    const newVariants = createForm.variants.filter((_, i) => i !== index);
    // Redistribute traffic
    const newTraffic = Math.floor(100 / newVariants.length);
    const remainder = 100 - (newTraffic * newVariants.length);
    
    const redistributed = newVariants.map((v, i) => ({
      ...v,
      traffic: newTraffic + (i === newVariants.length - 1 ? remainder : 0)
    }));
    
    setCreateForm({ ...createForm, variants: redistributed });
  };
  
  // Update variant traffic
  const updateVariantTraffic = (index, traffic) => {
    const newVariants = [...createForm.variants];
    newVariants[index].traffic = parseInt(traffic) || 0;
    setCreateForm({ ...createForm, variants: newVariants });
  };
  
  // Update variant name
  const updateVariantName = (index, name) => {
    const newVariants = [...createForm.variants];
    newVariants[index].name = name;
    setCreateForm({ ...createForm, variants: newVariants });
  };
  
  // Auto-split traffic equally
  const autoSplitTraffic = () => {
    const count = createForm.variants.length;
    const perVariant = Math.floor(100 / count);
    const remainder = 100 - (perVariant * count);
    
    const redistributed = createForm.variants.map((v, i) => ({
      ...v,
      traffic: perVariant + (i === count - 1 ? remainder : 0)
    }));
    
    setCreateForm({ ...createForm, variants: redistributed });
  };
  
  // Check if traffic totals 100%
  const totalTraffic = createForm.variants.reduce((sum, v) => sum + v.traffic, 0);

  const handleCreateTest = async (e) => {
    e.preventDefault();
    
    // Validate traffic totals 100%
    const total = createForm.variants.reduce((sum, v) => sum + v.traffic, 0);
    if (total !== 100) {
      toast.error(`Traffic must total 100% (currently ${total}%)`);
      return;
    }
    
    try {
      const token = localStorage.getItem('auth_token');
      // Convert to backend format
      const payload = {
        funnel_id: createForm.funnel_id,
        page_id: createForm.page_id,
        name: createForm.name,
        description: createForm.description,
        variants: createForm.variants,
        auto_winner_threshold: createForm.auto_winner_threshold
      };
      
      await axios.post(
        `${BACKEND_URL}/api/ab-tests`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('A/B test created with ' + createForm.variants.length + ' variants');
      setShowCreateModal(false);
      setCreateForm({
        funnel_id: '',
        page_id: '',
        name: '',
        description: '',
        variants: [
          { name: 'Control (A)', traffic: 50 },
          { name: 'Variant B', traffic: 50 }
        ],
        auto_winner_threshold: 100
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create test');
    }
  };

  const handleStartTest = async (testId) => {
    try {
      const token = localStorage.getItem('auth_token');
      await axios.post(
        `${BACKEND_URL}/api/ab-tests/${testId}/start`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('A/B test started');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start test');
    }
  };

  const handlePauseTest = async (testId) => {
    try {
      const token = localStorage.getItem('auth_token');
      await axios.post(
        `${BACKEND_URL}/api/ab-tests/${testId}/pause`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('A/B test paused');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to pause test');
    }
  };

  const handleResumeTest = async (testId) => {
    try {
      const token = localStorage.getItem('auth_token');
      await axios.post(
        `${BACKEND_URL}/api/ab-tests/${testId}/resume`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('A/B test resumed');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resume test');
    }
  };

  const handleDeclareWinner = async (testId, variantId) => {
    if (!window.confirm('Are you sure you want to declare this variant as the winner? This action cannot be undone.')) return;
    try {
      const token = localStorage.getItem('auth_token');
      await axios.post(
        `${BACKEND_URL}/api/ab-tests/${testId}/declare-winner`,
        { variant_id: variantId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Winner declared! The winning variant is now the default.');
      setShowDetailModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to declare winner');
    }
  };

  const handleDeleteTest = async (testId) => {
    if (!window.confirm('Are you sure you want to delete this A/B test?')) return;
    try {
      const token = localStorage.getItem('auth_token');
      await axios.delete(
        `${BACKEND_URL}/api/ab-tests/${testId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('A/B test deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete test');
    }
  };

  const openTestDetail = async (testId) => {
    try {
      const token = localStorage.getItem('auth_token');
      const res = await axios.get(
        `${BACKEND_URL}/api/ab-tests/${testId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSelectedTest(res.data);
      setShowDetailModal(true);
    } catch (error) {
      toast.error('Failed to load test details');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric'
    });
  };

  return (
    <div data-testid="ab-testing-page" className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2 flex items-center gap-3">
            <FlaskConical className="w-8 h-8 text-purple-400" />
            A/B Testing
          </h1>
          <p className="text-emerald-300/70">Test different page variants to maximize conversions</p>
        </div>
        <Button
          data-testid="create-ab-test-btn"
          onClick={() => setShowCreateModal(true)}
          className="bg-emerald-600 hover:bg-emerald-700 text-white"
        >
          <Plus className="w-4 h-4 mr-2" />
          New A/B Test
        </Button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <Card className="bg-[#052e22]/50 border-emerald-800/30">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-500/20 rounded-lg">
                  <FlaskConical className="w-5 h-5 text-purple-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{stats.total_tests}</p>
                  <p className="text-xs text-emerald-300/70">Total Tests</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-[#052e22]/50 border-emerald-800/30">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-500/20 rounded-lg">
                  <Play className="w-5 h-5 text-green-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{stats.running_tests}</p>
                  <p className="text-xs text-emerald-300/70">Running</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-[#052e22]/50 border-emerald-800/30">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-500/20 rounded-lg">
                  <Trophy className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{stats.completed_tests}</p>
                  <p className="text-xs text-emerald-300/70">Completed</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-[#052e22]/50 border-emerald-800/30">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-500/20 rounded-lg">
                  <Eye className="w-5 h-5 text-amber-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{stats.total_views}</p>
                  <p className="text-xs text-emerald-300/70">Total Views</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-[#052e22]/50 border-emerald-800/30">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-emerald-500/20 rounded-lg">
                  <TrendingUp className="w-5 h-5 text-emerald-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{stats.average_improvement}%</p>
                  <p className="text-xs text-emerald-300/70">Avg Improvement</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tests List */}
      <Card className="bg-[#052e22]/50 border-emerald-800/30">
        <CardHeader className="border-b border-emerald-800/30">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg font-semibold text-white">A/B Tests</CardTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={fetchData}
              className="text-emerald-400 hover:bg-emerald-900/30"
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-6 h-6 text-emerald-400 animate-spin" />
            </div>
          ) : tests.length === 0 ? (
            <div className="text-center py-12">
              <FlaskConical className="w-12 h-12 text-emerald-800 mx-auto mb-3" />
              <p className="text-emerald-300/50">No A/B tests yet</p>
              <p className="text-emerald-300/30 text-sm mt-1">Create your first test to start optimizing</p>
            </div>
          ) : (
            <div className="divide-y divide-emerald-800/30">
              {tests.map((test) => (
                <div
                  key={test.id}
                  className="flex items-center justify-between p-4 hover:bg-emerald-900/20 transition-colors cursor-pointer"
                  onClick={() => openTestDetail(test.id)}
                >
                  <div className="flex items-center gap-4">
                    <div className={`p-2 rounded-lg ${
                      test.status === 'running' ? 'bg-green-500/20' :
                      test.status === 'completed' ? 'bg-blue-500/20' :
                      test.status === 'paused' ? 'bg-amber-500/20' : 'bg-slate-500/20'
                    }`}>
                      <ArrowLeftRight className={`w-5 h-5 ${
                        test.status === 'running' ? 'text-green-400' :
                        test.status === 'completed' ? 'text-blue-400' :
                        test.status === 'paused' ? 'text-amber-400' : 'text-slate-400'
                      }`} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="text-white font-medium">{test.name}</p>
                        <Badge className={STATUS_COLORS[test.status]}>{test.status}</Badge>
                        {test.has_winner && (
                          <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30">
                            <Trophy className="w-3 h-3 mr-1" />
                            {test.winner_name}
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-emerald-300/70">
                        {test.funnel_name} / {test.page_name}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-6">
                    <div className="text-center">
                      <p className="text-white font-medium">{test.traffic_split}%</p>
                      <p className="text-xs text-emerald-300/50">Split</p>
                    </div>
                    <div className="text-center">
                      <p className="text-white font-medium">{test.total_views}</p>
                      <p className="text-xs text-emerald-300/50">Views</p>
                    </div>
                    <div className="text-center">
                      <p className="text-white font-medium">{test.total_conversions}</p>
                      <p className="text-xs text-emerald-300/50">Conversions</p>
                    </div>
                    <div className="text-center">
                      <p className={`font-medium flex items-center gap-1 ${
                        test.improvement > 0 ? 'text-green-400' :
                        test.improvement < 0 ? 'text-red-400' : 'text-slate-400'
                      }`}>
                        {test.improvement > 0 ? <TrendingUp className="w-3 h-3" /> : 
                         test.improvement < 0 ? <TrendingDown className="w-3 h-3" /> : null}
                        {test.improvement > 0 ? '+' : ''}{test.improvement}%
                      </p>
                      <p className="text-xs text-emerald-300/50">Improvement</p>
                    </div>
                    <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                      {test.status === 'draft' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleStartTest(test.id)}
                          className="text-green-400 hover:bg-green-900/30"
                        >
                          <Play className="w-4 h-4" />
                        </Button>
                      )}
                      {test.status === 'running' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handlePauseTest(test.id)}
                          className="text-amber-400 hover:bg-amber-900/30"
                        >
                          <Pause className="w-4 h-4" />
                        </Button>
                      )}
                      {test.status === 'paused' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleResumeTest(test.id)}
                          className="text-green-400 hover:bg-green-900/30"
                        >
                          <Play className="w-4 h-4" />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteTest(test.id)}
                        className="text-red-400 hover:bg-red-900/30"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Modal */}
      <AnimatePresence>
        {showCreateModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowCreateModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-[#0a3d2e] border border-emerald-800/30 rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-white">Create Multivariate A/B Test</h2>
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="p-2 hover:bg-emerald-900/50 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-emerald-400" />
                </button>
              </div>
              <form onSubmit={handleCreateTest} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-emerald-300 mb-2">Test Name *</label>
                  <input
                    type="text"
                    required
                    value={createForm.name}
                    onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                    placeholder="e.g., Homepage Hero Test"
                    className="w-full px-4 py-3 bg-[#052e22] border border-emerald-800/50 rounded-lg text-white placeholder-emerald-500/50 focus:outline-none focus:border-emerald-500"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-emerald-300 mb-2">Funnel *</label>
                    <select
                      required
                      value={createForm.funnel_id}
                      onChange={(e) => handleFunnelChange(e.target.value)}
                      className="w-full px-4 py-3 bg-[#052e22] border border-emerald-800/50 rounded-lg text-white focus:outline-none focus:border-emerald-500"
                    >
                      <option value="">Select a funnel</option>
                      {funnels.map((funnel) => (
                        <option key={funnel.id} value={funnel.id}>{funnel.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-emerald-300 mb-2">Page *</label>
                    <select
                      required
                      value={createForm.page_id}
                      onChange={(e) => setCreateForm({ ...createForm, page_id: e.target.value })}
                      disabled={!createForm.funnel_id}
                      className="w-full px-4 py-3 bg-[#052e22] border border-emerald-800/50 rounded-lg text-white focus:outline-none focus:border-emerald-500 disabled:opacity-50"
                    >
                      <option value="">Select a page</option>
                      {pages.map((page) => (
                        <option key={page.id} value={page.id}>{page.name}</option>
                      ))}
                    </select>
                  </div>
                </div>
                
                {/* Multivariate Variants Section */}
                <div className="border border-emerald-800/30 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="text-lg font-semibold text-white">Test Variants</h3>
                      <p className="text-xs text-emerald-300/50">Configure up to 4 variants (A/B/C/D)</p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={autoSplitTraffic}
                        className="border-emerald-500/50 text-emerald-400 hover:bg-emerald-900/30"
                      >
                        <RefreshCw className="w-3 h-3 mr-1" />
                        Auto Split
                      </Button>
                      {createForm.variants.length < 4 && (
                        <Button
                          type="button"
                          size="sm"
                          onClick={addVariant}
                          className="bg-purple-600 hover:bg-purple-700 text-white"
                        >
                          <Plus className="w-4 h-4 mr-1" />
                          Add Variant
                        </Button>
                      )}
                    </div>
                  </div>
                  
                  {/* Traffic Total Indicator */}
                  <div className={`flex items-center gap-2 mb-4 p-2 rounded-lg ${
                    totalTraffic === 100 ? 'bg-green-500/20' : 'bg-red-500/20'
                  }`}>
                    <Percent className={`w-4 h-4 ${totalTraffic === 100 ? 'text-green-400' : 'text-red-400'}`} />
                    <span className={`text-sm font-medium ${totalTraffic === 100 ? 'text-green-400' : 'text-red-400'}`}>
                      Total Traffic: {totalTraffic}% {totalTraffic !== 100 && '(must equal 100%)'}
                    </span>
                  </div>
                  
                  {/* Variants Grid */}
                  <div className="grid grid-cols-2 gap-3">
                    {createForm.variants.map((variant, index) => {
                      const variantLetters = ['A', 'B', 'C', 'D'];
                      const variantColors = [
                        'border-blue-500/50 bg-blue-500/10',
                        'border-purple-500/50 bg-purple-500/10',
                        'border-amber-500/50 bg-amber-500/10',
                        'border-pink-500/50 bg-pink-500/10'
                      ];
                      return (
                        <div
                          key={index}
                          className={`p-3 rounded-lg border-2 ${variantColors[index]}`}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <span className={`px-2 py-0.5 rounded text-xs font-bold ${
                              index === 0 ? 'bg-blue-500 text-white' :
                              index === 1 ? 'bg-purple-500 text-white' :
                              index === 2 ? 'bg-amber-500 text-white' :
                              'bg-pink-500 text-white'
                            }`}>
                              {variantLetters[index]}
                            </span>
                            {index > 1 && (
                              <button
                                type="button"
                                onClick={() => removeVariant(index)}
                                className="p-1 hover:bg-red-500/30 rounded text-red-400"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                          <input
                            type="text"
                            value={variant.name}
                            onChange={(e) => updateVariantName(index, e.target.value)}
                            placeholder="Variant name"
                            className="w-full px-2 py-1.5 bg-[#052e22] border border-emerald-800/50 rounded text-white text-sm mb-2 focus:outline-none focus:border-emerald-500"
                          />
                          <div className="flex items-center gap-2">
                            <input
                              type="number"
                              min="1"
                              max="99"
                              value={variant.traffic}
                              onChange={(e) => updateVariantTraffic(index, e.target.value)}
                              className="w-16 px-2 py-1.5 bg-[#052e22] border border-emerald-800/50 rounded text-white text-sm text-center focus:outline-none focus:border-emerald-500"
                            />
                            <span className="text-emerald-300/70 text-sm">% traffic</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  
                  {/* Visual Traffic Bar */}
                  <div className="mt-4">
                    <p className="text-xs text-emerald-300/50 mb-2">Traffic Distribution</p>
                    <div className="h-4 bg-[#052e22] rounded-full overflow-hidden flex">
                      {createForm.variants.map((variant, index) => {
                        const colors = ['bg-blue-500', 'bg-purple-500', 'bg-amber-500', 'bg-pink-500'];
                        return (
                          <div
                            key={index}
                            className={`${colors[index]} transition-all duration-300`}
                            style={{ width: `${variant.traffic}%` }}
                            title={`${variant.name}: ${variant.traffic}%`}
                          />
                        );
                      })}
                    </div>
                    <div className="flex mt-1">
                      {createForm.variants.map((variant, index) => {
                        const colors = ['text-blue-400', 'text-purple-400', 'text-amber-400', 'text-pink-400'];
                        return (
                          <div
                            key={index}
                            className={`text-xs ${colors[index]} font-medium`}
                            style={{ width: `${variant.traffic}%` }}
                          >
                            {variant.traffic > 15 && `${variant.traffic}%`}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-emerald-300 mb-2">
                    Auto-declare winner after conversions
                  </label>
                  <input
                    type="number"
                    min="10"
                    max="10000"
                    value={createForm.auto_winner_threshold}
                    onChange={(e) => setCreateForm({ ...createForm, auto_winner_threshold: parseInt(e.target.value) })}
                    className="w-full px-4 py-3 bg-[#052e22] border border-emerald-800/50 rounded-lg text-white focus:outline-none focus:border-emerald-500"
                  />
                  <p className="text-xs text-emerald-300/50 mt-1">Winner declared automatically after this many total conversions</p>
                </div>
                <Button
                  type="submit"
                  disabled={totalTraffic !== 100}
                  className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-3 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <FlaskConical className="w-4 h-4 mr-2" />
                  Create {createForm.variants.length}-Variant Test
                </Button>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Detail Modal */}
      <AnimatePresence>
        {showDetailModal && selectedTest && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowDetailModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-[#0a3d2e] border border-emerald-800/30 rounded-xl p-6 w-full max-w-3xl max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-xl font-bold text-white">{selectedTest.name}</h2>
                  <Badge className={STATUS_COLORS[selectedTest.status]}>{selectedTest.status}</Badge>
                </div>
                <button
                  onClick={() => setShowDetailModal(false)}
                  className="p-2 hover:bg-emerald-900/50 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-emerald-400" />
                </button>
              </div>

              {/* Test Info */}
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="bg-[#052e22] p-4 rounded-lg">
                  <p className="text-sm text-emerald-300/70 mb-1">Traffic Split</p>
                  <p className="text-xl font-bold text-white">{selectedTest.traffic_split}%</p>
                </div>
                <div className="bg-[#052e22] p-4 rounded-lg">
                  <p className="text-sm text-emerald-300/70 mb-1">Total Views</p>
                  <p className="text-xl font-bold text-white">{selectedTest.total_views}</p>
                </div>
                <div className="bg-[#052e22] p-4 rounded-lg">
                  <p className="text-sm text-emerald-300/70 mb-1">Total Conversions</p>
                  <p className="text-xl font-bold text-white">{selectedTest.total_conversions}</p>
                </div>
              </div>

              {/* Variants Comparison */}
              <h3 className="text-lg font-semibold text-white mb-4">Variants Comparison</h3>
              <div className={`grid gap-4 mb-6 ${
                selectedTest.variants?.length === 2 ? 'grid-cols-2' :
                selectedTest.variants?.length === 3 ? 'grid-cols-3' :
                'grid-cols-2 md:grid-cols-4'
              }`}>
                {selectedTest.variants?.map((variant, index) => {
                  const variantLetters = ['A', 'B', 'C', 'D'];
                  const borderColors = [
                    'border-blue-500/30',
                    'border-purple-500/30',
                    'border-amber-500/30',
                    'border-pink-500/30'
                  ];
                  const badgeColors = [
                    'bg-blue-500/20 text-blue-400',
                    'bg-purple-500/20 text-purple-400',
                    'bg-amber-500/20 text-amber-400',
                    'bg-pink-500/20 text-pink-400'
                  ];
                  
                  // Find the winner (highest conversion rate)
                  const maxConversionRate = Math.max(...(selectedTest.variants?.map(v => parseFloat(v.conversion_rate) || 0) || [0]));
                  const isLeading = parseFloat(variant.conversion_rate) === maxConversionRate && maxConversionRate > 0;
                  
                  return (
                    <div
                      key={variant.id}
                      className={`bg-[#052e22] p-4 rounded-lg border-2 ${
                        variant.is_winner ? 'border-yellow-500' : borderColors[index]
                      } ${isLeading && !variant.is_winner ? 'ring-2 ring-green-500/50' : ''}`}
                    >
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${badgeColors[index]}`}>
                            {variantLetters[index]}
                          </span>
                          <span className="text-white font-medium text-sm truncate">{variant.name}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          {isLeading && !variant.is_winner && (
                            <Award className="w-4 h-4 text-green-400" title="Currently Leading" />
                          )}
                          {variant.is_winner && (
                            <Trophy className="w-4 h-4 text-yellow-400" title="Winner" />
                          )}
                        </div>
                      </div>
                      <div className="space-y-3">
                        <div className="flex justify-between">
                          <span className="text-emerald-300/70 text-xs">Traffic</span>
                          <span className="text-white font-medium text-sm">{variant.traffic || 50}%</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-emerald-300/70 text-xs">Views</span>
                          <span className="text-white font-medium text-sm">{variant.views}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-emerald-300/70 text-xs">Conversions</span>
                          <span className="text-white font-medium text-sm">{variant.conversions}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-emerald-300/70 text-xs">Conv Rate</span>
                          <span className={`font-medium text-sm ${isLeading ? 'text-green-400' : 'text-emerald-400'}`}>
                            {variant.conversion_rate}%
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-emerald-300/70 text-xs">Revenue</span>
                          <span className="text-white font-medium text-sm">₹{variant.revenue}</span>
                        </div>
                      </div>
                      
                      {/* Statistical Significance */}
                      {index > 0 && variant.significance && (
                        <div className="mt-3 pt-3 border-t border-emerald-800/30">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-emerald-300/70 text-xs">Confidence</span>
                            <span className={`text-sm font-medium ${
                              variant.significance.significant ? 'text-green-400' : 'text-amber-400'
                            }`}>
                              {variant.significance.confidence}%
                            </span>
                          </div>
                          {/* Confidence progress bar */}
                          <div className="w-full h-1.5 bg-[#0a0f1e] rounded-full overflow-hidden mb-2">
                            <div 
                              className={`h-full transition-all ${
                                variant.significance.significant ? 'bg-green-500' : 'bg-amber-500'
                              }`}
                              style={{ width: `${Math.min(variant.significance.confidence, 100)}%` }}
                            />
                          </div>
                          <p className={`text-xs ${
                            variant.significance.significant ? 'text-green-400' : 'text-amber-400'
                          }`}>
                            {variant.significance.significant ? '✅' : '⏳'} {variant.significance.recommendation}
                          </p>
                          {variant.significance.lift !== 0 && (
                            <p className={`text-xs mt-1 ${
                              variant.significance.lift > 0 ? 'text-green-300' : 'text-red-300'
                            }`}>
                              Lift: {variant.significance.lift > 0 ? '+' : ''}{variant.significance.lift}%
                            </p>
                          )}
                        </div>
                      )}
                      
                      {selectedTest.status === 'running' && !selectedTest.winner_variant_id && (
                        <Button
                          onClick={() => handleDeclareWinner(selectedTest.id, variant.id)}
                          size="sm"
                          className="w-full mt-3 bg-yellow-500/20 hover:bg-yellow-500/30 text-yellow-400 border border-yellow-500/30"
                        >
                          <Trophy className="w-3 h-3 mr-1" />
                          Declare Winner
                        </Button>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Actions */}
              <div className="flex gap-3">
                {selectedTest.status === 'draft' && (
                  <Button
                    onClick={() => {
                      handleStartTest(selectedTest.id);
                      setShowDetailModal(false);
                    }}
                    className="bg-green-600 hover:bg-green-700 text-white"
                  >
                    <Play className="w-4 h-4 mr-2" />
                    Start Test
                  </Button>
                )}
                {selectedTest.status === 'running' && (
                  <Button
                    onClick={() => {
                      handlePauseTest(selectedTest.id);
                      setShowDetailModal(false);
                    }}
                    className="bg-amber-600 hover:bg-amber-700 text-white"
                  >
                    <Pause className="w-4 h-4 mr-2" />
                    Pause Test
                  </Button>
                )}
                {selectedTest.status === 'paused' && (
                  <Button
                    onClick={() => {
                      handleResumeTest(selectedTest.id);
                      setShowDetailModal(false);
                    }}
                    className="bg-green-600 hover:bg-green-700 text-white"
                  >
                    <Play className="w-4 h-4 mr-2" />
                    Resume Test
                  </Button>
                )}
                <Button
                  onClick={() => {
                    handleDeleteTest(selectedTest.id);
                    setShowDetailModal(false);
                  }}
                  variant="outline"
                  className="border-red-500/50 text-red-400 hover:bg-red-900/30"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete Test
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ABTesting;
